import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { AlertTriangle, Plus, Save, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const severityColors = {
  low: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  critical: 'bg-red-100 text-red-800 border-red-200'
};

const severityIcons = {
  low: <AlertTriangle className="h-4 w-4 text-green-600" />,
  medium: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
  critical: <AlertTriangle className="h-4 w-4 text-red-600" />
};

const CreateIssueForm = ({ 
  productId, 
  currentUser, 
  onSubmit, 
  onCancel,
  isOpen = false,
  onClose,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: 'medium'
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.severity) {
      newErrors.severity = 'Severity is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const issueData = {
      product_id: productId,
      title: formData.title.trim(),
      description: formData.description.trim(),
      severity: formData.severity,
      created_by: currentUser || 'Unknown User'
    };

    try {
      const response = await api.post('/issues', issueData);

      const result = response.data;

      if (result.success) {
        toast({
          title: "Success",
          description: "Issue created successfully",
        });
        
        // Reset form
        setFormData({
          title: '',
          description: '',
          severity: 'medium'
        });
        setErrors({});
        
        // Call parent callback
        if (onSubmit) {
          onSubmit(result.data);
        }
        
        if (onClose) {
          onClose();
        }
      } else {
        throw new Error(result.message || 'Failed to create issue');
      }
    } catch (error) {
      console.error('Error creating issue:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create issue",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const getSeverityColor = () => {
    return severityColors[formData.severity] || severityColors.medium;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogTrigger asChild>
        <Button className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Known Issue
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Create New Known Issue
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Severity Preview */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                {severityIcons[formData.severity]}
                <div>
                  <p className="font-semibold">Issue Severity</p>
                  <Badge className={getSeverityColor()}>
                    {formData.severity.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Title */}
          <div className="space-y-2">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Issue Title *
            </label>
            <Input
              id="title"
              type="text"
              placeholder="Brief description of the issue..."
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={errors.title ? 'border-red-500' : ''}
              disabled={loading}
            />
            {errors.title && (
              <p className="text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Severity */}
          <div className="space-y-2">
            <label htmlFor="severity" className="block text-sm font-medium text-gray-700">
              Severity Level *
            </label>
            <Select 
              value={formData.severity} 
              onValueChange={(value) => handleInputChange('severity', value)}
              disabled={loading}
            >
              <SelectTrigger className={errors.severity ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select severity level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">
                  <div className="flex items-center gap-2">
                    {severityIcons.low}
                    <span>Low - Minor issue, limited impact</span>
                  </div>
                </SelectItem>
                <SelectItem value="medium">
                  <div className="flex items-center gap-2">
                    {severityIcons.medium}
                    <span>Medium - Moderate impact</span>
                  </div>
                </SelectItem>
                <SelectItem value="critical">
                  <div className="flex items-center gap-2">
                    {severityIcons.critical}
                    <span>Critical - Major impact</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {errors.severity && (
              <p className="text-sm text-red-600">{errors.severity}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description *
            </label>
            <Textarea
              id="description"
              placeholder="Detailed description of the issue, including any relevant context, reproduction steps, or expected behavior..."
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={6}
              className={errors.description ? 'border-red-500' : ''}
              disabled={loading}
            />
            {errors.description && (
              <p className="text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Preview */}
          {formData.title && formData.description && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  {severityIcons[formData.severity]}
                  Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-sm text-gray-600 mb-1">Title</h4>
                    <p className="text-gray-900">{formData.title}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-600 mb-1">Description</h4>
                    <p className="text-gray-700 whitespace-pre-wrap text-sm">
                      {formData.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Create Issue
                </>
              )}
            </Button>
            
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel || onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateIssueForm;